import { LatLngLiteral } from "leaflet";

import Map from "../redux/services/Map";
import Address from "../types/Address";
import Language from "../services/Language";
import { findValueByLanguageKey } from "../assets/languages/langs";

import addressToString from "./addressToString";

function pickLanguageData(
	language?: Record<string, Record<string, string> | string>,
): Record<string, string> | undefined {
	if (!language) return undefined;

	const data =
		findValueByLanguageKey(language as Partial<Record<Language, string>>) ||
		language.en ||
		language.ru ||
		language.uk;

	return typeof data === "object" && data !== null
		? (data as Record<string, string>)
		: undefined;
}

function pickAdminPlaceSettlement(
	objects: Map.Object[],
	preferredSettlement?: string,
): Record<string, string> | undefined {
	const adminPlaces = objects.filter(
		(object) => object.type === "adminPlace",
	);
	if (!adminPlaces.length) return undefined;

	const preferred = preferredSettlement?.trim().toLowerCase();
	const matched = preferred
		? adminPlaces.find((object) => {
				const data = pickLanguageData(object.properties?.language);
				const name = (
					data?.name ||
					data?.settlement ||
					""
				).toLowerCase();
				return name === preferred;
		  })
		: undefined;

	const chosen = matched || adminPlaces[0];
	const data = pickLanguageData(chosen?.properties?.language);
	if (!data) return undefined;

	// Visicom adminPlace stores the settlement name in `name`, not `settlement`.
	return {
		...data,
		settlement: data.settlement || data.name || "",
	};
}

async function reveal(
	coordinates: LatLngLiteral,
	language: Language,
	taxiServiceId?: number,
) {
	const { objects } = await Map.Point.reveal(coordinates, {
		language,
		taxiServiceId,
	});

	const addressObj = objects.find((object) => object.type === "address");
	const streetObj = objects.find((object) => object.type === "street");
	const settlementObj = objects.find(
		(object) => object.type === "settlement",
	);
	const districtObj = objects.find((object) => object.type === "district");
	const regionObj = objects.find((object) => object.type === "region");

	const addressData = pickLanguageData(addressObj?.properties?.language);
	const streetData = pickLanguageData(streetObj?.properties?.language);
	const settlementData = pickLanguageData(
		settlementObj?.properties?.language,
	);
	const districtData = pickLanguageData(districtObj?.properties?.language);
	const regionData = pickLanguageData(regionObj?.properties?.language);
	const adminPlaceData = pickAdminPlaceSettlement(
		objects,
		streetData?.settlement ||
			addressData?.settlement ||
			settlementData?.settlement,
	);

	const hasAddressWithStreetOrHouse = Boolean(
		addressData &&
			(addressData.street || addressData.number || addressData.house),
	);

	const settlement =
		settlementData?.settlement ||
		streetData?.settlement ||
		addressData?.settlement ||
		adminPlaceData?.settlement ||
		"";
	const settlementType =
		settlementData?.settlementType ||
		streetData?.settlementType ||
		addressData?.settlementType ||
		adminPlaceData?.settlementType ||
		"";
	const district =
		addressData?.district ||
		streetData?.district ||
		settlementData?.district ||
		adminPlaceData?.district ||
		districtData?.district ||
		"";
	const region =
		addressData?.region ||
		streetData?.region ||
		settlementData?.region ||
		adminPlaceData?.region ||
		districtData?.region ||
		regionData?.region ||
		"";
	const street = addressData?.street || streetData?.street || "";
	const streetType = addressData?.streetType || streetData?.streetType || "";
	const house =
		addressData?.number ||
		addressData?.house ||
		streetData?.number ||
		streetData?.house ||
		"";

	const data: Record<string, string> = hasAddressWithStreetOrHouse
		? {
				...(settlementData ?? {}),
				...(adminPlaceData ?? {}),
				...(districtData ?? {}),
				...(regionData ?? {}),
				...(streetData ?? {}),
				...addressData,
				settlement:
					addressData?.settlement ||
					settlementData?.settlement ||
					streetData?.settlement ||
					adminPlaceData?.settlement ||
					"",
				settlementType:
					addressData?.settlementType ||
					settlementData?.settlementType ||
					streetData?.settlementType ||
					adminPlaceData?.settlementType ||
					"",
				district:
					addressData?.district ||
					streetData?.district ||
					settlementData?.district ||
					adminPlaceData?.district ||
					districtData?.district ||
					"",
				region:
					addressData?.region ||
					streetData?.region ||
					settlementData?.region ||
					adminPlaceData?.region ||
					districtData?.region ||
					regionData?.region ||
					"",
		  }
		: {
				country:
					addressData?.country ||
					streetData?.country ||
					settlementData?.country ||
					adminPlaceData?.country ||
					districtData?.country ||
					regionData?.country ||
					"",
				countryCode:
					addressData?.countryCode ||
					streetData?.countryCode ||
					settlementData?.countryCode ||
					adminPlaceData?.countryCode ||
					districtData?.countryCode ||
					regionData?.countryCode ||
					"",
				settlement,
				settlementType,
				district,
				region,
				street,
				streetType,
				number: house,
		  };

	const processedAddress: Partial<Address> = {
		country: data.country ?? "",
		countryCode: data.countryCode ?? "",
		street: data.street ?? street,
		streetType: data.streetType ?? streetType,
		settlement: data.settlement ?? settlement,
		settlementType: data.settlementType ?? settlementType,
		district: data.district ?? district,
		region: data.region ?? region,
		house: data.number ?? data.house ?? house,
	};

	processedAddress.address = addressToString(processedAddress);

	return processedAddress;
}

export default reveal;
